import { useState } from 'react';
import { PageHeader } from '@/components/PageHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { 
  Building, 
  Home, 
  MapPin, 
  Users, 
  Zap, 
  FileText, 
  TrendingUp, 
  Plus,
  Search,
  Edit,
  Eye,
  BarChart3,
  Calendar,
  DollarSign,
  AlertCircle,
  CheckCircle,
  Clock
} from 'lucide-react';
import { useTumProperties } from '@/hooks/useTumProperties';
import { useTumUnits } from '@/hooks/useTumUnits';
import { useTumUtilityAccounts } from '@/hooks/useTumUtilityAccounts';
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { useWorkspace } from '@/contexts/WorkspaceContext';
import { TumPropertyForm } from '@/components/tum/TumPropertyForm';
import { format } from 'date-fns';

export default function TumProperties() {
  const { currentWorkspace } = useWorkspace();
  const { properties, isLoading: propertiesLoading } = useTumProperties();
  const { units, isLoading: unitsLoading } = useTumUnits();
  const { accounts, isLoading: accountsLoading } = useTumUtilityAccounts();
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [selectedProperty, setSelectedProperty] = useState<string | null>(null);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [activeTab, setActiveTab] = useState('overview');

  // Fetch bills for properties
  const { data: billsData } = useQuery({
    queryKey: ['tum-property-bills', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase
        .from('bills')
        .select('*, property:tum_properties(id, property_name)')
        .eq('workspace_id', currentWorkspace.id)
        .not('tum_property_id', 'is', null)
        .order('bill_date', { ascending: false })
        .limit(100);
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id,
  });

  // Fetch sites for properties
  const { data: sitesData } = useQuery({
    queryKey: ['tum-sites', currentWorkspace?.id],
    queryFn: async () => {
      if (!currentWorkspace?.id) return [];
      const { data, error } = await supabase
        .from('tum_sites')
        .select('*, property:tum_properties(id, property_name)')
        .eq('workspace_id', currentWorkspace.id);
      if (error) throw error;
      return data || [];
    },
    enabled: !!currentWorkspace?.id,
  });

  const filteredProperties = properties.filter(property => {
    const matchesSearch = property.property_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      property.property_code?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesStatus = statusFilter === 'all' || property.status === statusFilter;
    return matchesSearch && matchesStatus;
  });

  const selectedPropertyData = selectedProperty 
    ? properties.find(p => p.id === selectedProperty)
    : null;

  // Calculate stats
  const totalProperties = properties.length;
  const activeProperties = properties.filter(p => p.status === 'active').length;
  const totalUnits = properties.reduce((sum, p) => sum + (p.total_units || 0), 0);
  const totalAccounts = accounts.filter(a => 
    selectedProperty ? a.property_id === selectedProperty : true
  ).length;
  const totalBills = billsData?.length || 0;
  const totalCost = billsData?.reduce((sum: number, bill: any) => 
    sum + (parseFloat(bill.amount?.toString() || '0')), 0) || 0;

  // Get property-specific data
  const propertyUnits = selectedProperty 
    ? units.filter(u => u.property_id === selectedProperty)
    : [];
  const propertySites = selectedProperty
    ? (sitesData || []).filter((s: any) => s.property_id === selectedProperty)
    : [];
  const propertyAccounts = selectedProperty
    ? accounts.filter(a => a.property_id === selectedProperty)
    : [];
  const propertyBills = selectedProperty
    ? (billsData || []).filter((b: any) => b.property_id === selectedProperty)
    : [];

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(amount);
  };

  const formatDate = (date: string | Date) => {
    try {
      return format(new Date(date), 'MMM dd, yyyy');
    } catch {
      return 'N/A';
    }
  };

  if (propertiesLoading) {
    return (
      <div className="container mx-auto p-6">
        <div className="text-center py-12">
          <Clock className="h-8 w-8 mx-auto mb-2 animate-spin text-muted-foreground" />
          <p className="text-muted-foreground">Loading properties...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="container mx-auto p-6 space-y-6">
      <PageHeader 
        title="Properties" 
        description="Manage properties, sites, units, and utility accounts"
      />
      
      {/* Stats Overview */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Properties</CardTitle>
            <Building className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalProperties}</div>
            <p className="text-xs text-muted-foreground">
              {activeProperties} active
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Units</CardTitle>
            <Home className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalUnits}</div>
            <p className="text-xs text-muted-foreground">
              Across all properties
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Utility Accounts</CardTitle>
            <Zap className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalAccounts}</div>
            <p className="text-xs text-muted-foreground">
              {selectedProperty ? 'For selected property' : 'Total accounts'}
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
            <CardTitle className="text-sm font-medium">Total Bills</CardTitle>
            <FileText className="h-4 w-4 text-muted-foreground" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl font-bold">{totalBills}</div>
            <p className="text-xs text-muted-foreground">
              {formatCurrency(totalCost)} total cost
            </p>
          </CardContent>
        </Card>
      </div>

      {/* Filters and Actions */}
      <div className="flex justify-between items-center gap-4">
        <div className="flex gap-4 flex-1">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search properties..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10"
            />
          </div>
          <select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            className="px-3 py-2 border rounded-md text-sm"
            aria-label="Filter by status"
          >
            <option value="all">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
            <option value="onboarding">Onboarding</option>
          </select>
        </div>

        <Dialog open={isCreateDialogOpen} onOpenChange={setIsCreateDialogOpen}>
          <DialogTrigger asChild>
            <Button>
              <Plus className="h-4 w-4 mr-2" />
              Add Property
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>Add New Property</DialogTitle>
            </DialogHeader>
            <TumPropertyForm onSuccess={() => setIsCreateDialogOpen(false)} />
          </DialogContent>
        </Dialog>
      </div>

      {/* Main Content */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Properties List */}
        <div className="lg:col-span-1">
          <Card>
            <CardHeader>
              <CardTitle>Properties</CardTitle>
              <CardDescription>
                {filteredProperties.length} property{filteredProperties.length !== 1 ? 'ies' : ''} found
              </CardDescription>
            </CardHeader>
            <CardContent className="p-0">
              <div className="max-h-[600px] overflow-y-auto">
                {filteredProperties.length === 0 ? (
                  <div className="p-8 text-center text-muted-foreground">
                    <Building className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No properties found</p>
                    <p className="text-sm mt-1">Create your first property to get started</p>
                  </div>
                ) : (
                  <div className="divide-y">
                    {filteredProperties.map(property => (
                      <div
                        key={property.id}
                        onClick={() => {
                          setSelectedProperty(property.id);
                          setActiveTab('overview');
                        }}
                        className={`p-4 cursor-pointer hover:bg-muted/50 transition-colors ${
                          selectedProperty === property.id ? 'bg-muted' : ''
                        }`}
                      >
                        <div className="flex items-start justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1">
                              <h3 className="font-semibold">{property.property_name}</h3>
                              <Badge variant={property.status === 'active' ? 'default' : 'secondary'}>
                                {property.status}
                              </Badge>
                            </div>
                            {property.property_code && (
                              <p className="text-xs text-muted-foreground mb-1">
                                Code: {property.property_code}
                              </p>
                            )}
                            <div className="flex items-center gap-2 text-xs text-muted-foreground">
                              <MapPin className="h-3 w-3" />
                              <span>
                                {(property.address as any)?.city || 'N/A'}, {(property.address as any)?.state || ''}
                              </span>
                            </div>
                            <div className="flex items-center gap-4 mt-2 text-xs text-muted-foreground">
                              <span>{property.total_units || 0} units</span>
                              <span className="capitalize">{property.property_type}</span>
                            </div>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
          </div>
            </CardContent>
          </Card>
        </div>

        {/* Property Details */}
        <div className="lg:col-span-2">
          {selectedPropertyData ? (
            <Card>
              <CardHeader>
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="text-2xl">{selectedPropertyData.property_name}</CardTitle>
                    <CardDescription className="mt-1">
                      {selectedPropertyData.property_code && `Code: ${selectedPropertyData.property_code} • `}
                      {selectedPropertyData.property_type && `${selectedPropertyData.property_type} • `}
                      {selectedPropertyData.total_units || 0} units
                    </CardDescription>
                  </div>
                  <Badge variant={selectedPropertyData.status === 'active' ? 'default' : 'secondary'}>
                    {selectedPropertyData.status}
                  </Badge>
                </div>
              </CardHeader>
              <CardContent>
                <Tabs value={activeTab} onValueChange={setActiveTab}>
                  <TabsList className="grid w-full grid-cols-5">
                    <TabsTrigger value="overview">Overview</TabsTrigger>
                    <TabsTrigger value="units">Units</TabsTrigger>
                    <TabsTrigger value="sites">Sites</TabsTrigger>
                    <TabsTrigger value="accounts">Accounts</TabsTrigger>
                    <TabsTrigger value="bills">Bills</TabsTrigger>
                  </TabsList>

                  <TabsContent value="overview" className="space-y-4 mt-4">
                    <div className="grid grid-cols-2 gap-4">
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Address</label>
                        <p className="text-sm mt-1">
                          {selectedPropertyData.address && typeof selectedPropertyData.address === 'object' ? (
                            <>
                              {(selectedPropertyData.address as any).street && (
                                <span>{(selectedPropertyData.address as any).street}<br /></span>
                              )}
                              {(selectedPropertyData.address as any).city && (
                                <span>{(selectedPropertyData.address as any).city}, </span>
                              )}
                              {(selectedPropertyData.address as any).state} {(selectedPropertyData.address as any).zip}
                            </>
                          ) : 'N/A'}
                        </p>
                      </div>
                      <div>
                        <label className="text-sm font-medium text-muted-foreground">Property Details</label>
                        <div className="text-sm mt-1 space-y-1">
                          <p>Type: <span className="capitalize">{selectedPropertyData.property_type}</span></p>
                          <p>Total Units: {selectedPropertyData.total_units || 0}</p>
                          {selectedPropertyData.total_sqm && (
                            <p>Total Area: {selectedPropertyData.total_sqm} sqm</p>
                          )}
                          {selectedPropertyData.year_built && (
                            <p>Year Built: {selectedPropertyData.year_built}</p>
                          )}
                        </div>
                      </div>
                      {selectedPropertyData.property_manager_name && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Property Manager</label>
                          <div className="text-sm mt-1">
                            <p>{selectedPropertyData.property_manager_name}</p>
                            {selectedPropertyData.property_manager_email && (
                              <p className="text-muted-foreground">{selectedPropertyData.property_manager_email}</p>
                            )}
                            {selectedPropertyData.property_manager_phone && (
                              <p className="text-muted-foreground">{selectedPropertyData.property_manager_phone}</p>
                            )}
                          </div>
                        </div>
                      )}
                      {selectedPropertyData.notes && (
                        <div>
                          <label className="text-sm font-medium text-muted-foreground">Notes</label>
                          <p className="text-sm mt-1">{selectedPropertyData.notes}</p>
                        </div>
                      )}
                    </div>

                    {/* Quick Stats */}
                    <div className="grid grid-cols-3 gap-4 pt-4 border-t">
                      <div>
                        <p className="text-sm text-muted-foreground">Units</p>
                        <p className="text-2xl font-bold">{propertyUnits.length}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Sites</p>
                        <p className="text-2xl font-bold">{propertySites.length}</p>
                      </div>
                      <div>
                        <p className="text-sm text-muted-foreground">Accounts</p>
                        <p className="text-2xl font-bold">{propertyAccounts.length}</p>
                      </div>
                    </div>
                  </TabsContent>

                  <TabsContent value="units" className="mt-4">
                    {unitsLoading ? (
                      <div className="text-center py-8 text-muted-foreground">Loading units...</div>
                    ) : propertyUnits.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Home className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No units found for this property</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Unit Number</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Floor</TableHead>
                            <TableHead>Size</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {propertyUnits.map(unit => (
                            <TableRow key={unit.id}>
                              <TableCell className="font-medium">{unit.unit_number}</TableCell>
                              <TableCell className="capitalize">{unit.unit_type || 'N/A'}</TableCell>
                              <TableCell>{unit.floor || 'N/A'}</TableCell>
                              <TableCell>{unit.sqm ? `${unit.sqm} sqm` : 'N/A'}</TableCell>
                              <TableCell>
                                <Badge variant={unit.status === 'occupied' ? 'default' : 'secondary'}>
                                  {unit.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>

                  <TabsContent value="sites" className="mt-4">
                    {propertySites.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Building className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No sites found for this property</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Site Name</TableHead>
                            <TableHead>Type</TableHead>
                            <TableHead>Units</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {propertySites.map((site: any) => (
                            <TableRow key={site.id}>
                              <TableCell className="font-medium">{site.site_name}</TableCell>
                              <TableCell className="capitalize">{site.site_type || 'N/A'}</TableCell>
                              <TableCell>{site.total_units || 0}</TableCell>
                              <TableCell>
                                <Badge variant={site.status === 'active' ? 'default' : 'secondary'}>
                                  {site.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>

                  <TabsContent value="accounts" className="mt-4">
                    {accountsLoading ? (
                      <div className="text-center py-8 text-muted-foreground">Loading accounts...</div>
                    ) : propertyAccounts.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <Zap className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No utility accounts found for this property</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Account Number</TableHead>
                            <TableHead>Commodity</TableHead>
                            <TableHead>Vendor</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {propertyAccounts.map(account => (
                            <TableRow key={account.id}>
                              <TableCell className="font-medium">{account.account_number}</TableCell>
                              <TableCell className="capitalize">{account.commodity || 'N/A'}</TableCell>
                              <TableCell>{(account as any).vendor?.vendor_name || 'N/A'}</TableCell>
                              <TableCell>
                                <Badge variant={account.status === 'active' ? 'default' : 'secondary'}>
                                  {account.status}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>

                  <TabsContent value="bills" className="mt-4">
                    {propertyBills.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <FileText className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No bills found for this property</p>
                      </div>
                    ) : (
                      <Table>
                        <TableHeader>
                          <TableRow>
                            <TableHead>Bill Date</TableHead>
                            <TableHead>Account</TableHead>
                            <TableHead>Commodity</TableHead>
                            <TableHead>Amount</TableHead>
                            <TableHead>Status</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {propertyBills.slice(0, 20).map((bill: any) => (
                            <TableRow key={bill.id}>
                              <TableCell>{bill.bill_date ? formatDate(bill.bill_date) : 'N/A'}</TableCell>
                              <TableCell>{bill.account_number || 'N/A'}</TableCell>
                              <TableCell className="capitalize">{bill.commodity || 'N/A'}</TableCell>
                              <TableCell>{formatCurrency(parseFloat(bill.amount?.toString() || '0'))}</TableCell>
                              <TableCell>
                                <Badge variant={bill.status === 'paid' ? 'default' : 'secondary'}>
                                  {bill.status || 'pending'}
                                </Badge>
                              </TableCell>
                            </TableRow>
                          ))}
                        </TableBody>
                      </Table>
                    )}
                  </TabsContent>
                </Tabs>
              </CardContent>
            </Card>
          ) : (
            <Card>
              <CardContent className="py-12 text-center">
                <Building className="h-16 w-16 mx-auto mb-4 text-muted-foreground opacity-50" />
                <h3 className="text-lg font-semibold mb-2">Select a Property</h3>
                <p className="text-muted-foreground">
                  Choose a property from the list to view details, units, sites, accounts, and bills.
                </p>
              </CardContent>
      </Card>
          )}
        </div>
      </div>
    </div>
  );
}
